import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { QueryFunctionContext, useInfiniteQuery } from 'react-query'
import { Column, SortingRule } from 'react-table'
import clsx from 'clsx'
import { faCommentAltEdit } from '@fortawesome/pro-regular-svg-icons'
import flatten from 'lodash/flatten'
import isEqual from 'lodash/isEqual'
import { useInView } from 'react-intersection-observer'

import placeService from '../../services/placeService'
import { Experience } from '../../types/experiences'
import { ExperienceEvaluation, experiencesPerPage } from '../../utils/config'

import TableSkeleton from '../TableSkeleton'
import ErrorPage from '../ErrorPage'
import EmptyContent from '../EmptyContent'
import Table from '../Table'
import Spinner from '../Spinner'

const ExperiencesView: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })

  const [orderByFilter, setOrderBy] = useState<SortingRule<Experience>[]>([])

  const handleSortByChange = useCallback(
    (orderBy: SortingRule<any>[]) => {
      if (isEqual(orderByFilter, orderBy)) return

      setOrderBy(orderBy)
    },
    [orderByFilter]
  )

  const {
    data: experiences,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['locationExperiences', id, orderByFilter],
    (context: QueryFunctionContext) =>
      placeService.getExperiences(id || '', {
        first: experiencesPerPage,
        skip: context.pageParam || 0,
        ...(orderByFilter && orderByFilter.length > 0
          ? { orderBy: `${orderByFilter[0].id}_${orderByFilter[0].desc ? 'DESC' : 'ASC'}` }
          : { orderBy: 'createdAt_DESC' }),
      }),
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < experiencesPerPage) return false
        return flatten(allPages).length
      },
    }
  )

  const columns = React.useMemo<Column<Experience>[]>(
    () => [
      {
        id: 'title',
        Header: 'Title & User',
        accessor: ({ title, author }) => (
          <>
            <p className="text-sm font-normal leading-5 text-gray-900">{title}</p>
            <p className="text-sm font-normal leading-5 text-gray-500">{author.user.displayName}</p>
          </>
        ),
      },
      {
        id: 'placeName',
        Header: 'Location & Area',
        accessor: ({ place }) => (
          <>
            <p className="text-sm font-normal leading-5 text-gray-900">{place.name}</p>
            <p className="text-sm font-normal leading-5 text-gray-500">{place.location.city}</p>
          </>
        ),
      },
      {
        id: 'evaluation',
        Header: 'Rating',
        accessor: ({ evaluation }) => (
          <span
            className={clsx({
              'tag capitalize': true,
              'positive-tag': evaluation === ExperienceEvaluation.POSITIVE,
              'alert-tag': evaluation === ExperienceEvaluation.NEUTRAL,
              'negative-tag': evaluation === ExperienceEvaluation.NEGATIVE,
            })}
          >
            {evaluation}
          </span>
        ),
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: ({ isValidated, isVerified }) => {
          if (isValidated) {
            return <span className="tag positive-tag">Verified</span>
          }

          if (isVerified) {
            return <span className="tag positive-tag">At Location</span>
          }

          return <span className="tag alert-tag">Unverified</span>
        },
      },
      {
        id: 'createdAt',
        Header: 'Date',
        accessor: ({ createdAt }) => new Date(createdAt).toLocaleDateString('en-GB'),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id }) => (
          <button
            onClick={() => {
              navigate(`/experiences/${id}`)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  if (isLoading)
    return <TableSkeleton headers={['Title', 'Location & Area', 'Customer', 'Rating', 'Status', 'Date', ' ']} />

  if (isError || !experiences) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  const experiencesData = flatten(experiences?.pages)

  if (experiencesData.length === 0) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No experiences found."
          description="This user has not written any review."
          icon={faCommentAltEdit}
        />
      </div>
    )
  }

  return (
    <div>
      <Table columns={columns} data={experiencesData} onSortBy={handleSortByChange} />
      {hasNextPage ? (
        <div ref={ref} className="mt-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default ExperiencesView
